import {useRouter} from 'next/router';
import {Flex, FlexProps, Text, keyframes} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {getLanguage} from '@eksab/util';

import type {CompetitionConfig} from '@eksab/features/play/types';

const slideAnimation = keyframes`
  80% {
    inset-inline-end: 2rem;
    transform: scale(0.8);
  }

  to {
    inset-inline-end: 0.75rem;
    transform: scale(1);
  }
`;

interface HeaderProps extends FlexProps {
  game: CompetitionConfig;
}

export function Header({game, ...props}: HeaderProps) {
  const {locale} = useRouter();

  const t = useTranslate();

  return (
    <Flex
      as="header"
      flexDir="column"
      gap={{base: 1, lg: 2}}
      justify="center"
      h={{base: 48, md: 60, lg: 72}}
      px={{base: 6, md: 12, lg: 16}}
      pe={{base: 28, lg: 40}}
      py="4"
      overflow="hidden"
      pos="relative"
      isolation="isolate"
      border={{base: borders[game.name] ?? 'none'}}
      bgGradient={brandsGradient[game.name]}
      _after={{
        content: '""',
        height: '100%',
        width: 'auto',
        pos: 'absolute',
        inset: 0,
        zIndex: -2,
        bg: game.image ? `url(${game.image.src}) no-repeat` : undefined,
        backgroundPositionX: getLanguage(locale) === 'en' ? 'right' : 'left',
        backgroundPositionY: 'center',
        bgSize: {base: `auto ${mobileBgSizes[game.name] ?? '55%'}`, md: `auto ${bgSizes[game.name] ?? '90%'}`},
        transition: 'background-position-x 0.5s ease-in-out',
        insetEnd: '-50%',
        transform: 'scale(2)',
        animation: `${slideAnimation} 1s ease-in-out forwards`,
      }}
      rounded={{base: '3xl', lg: '2.125rem'}}
      {...props}
    >
      <Text
        as="h1"
        fontSize={{base: '2xl', md: '3xl', lg: '4xl'}}
        color={titleColors[game.name] ?? 'white'}
        fontWeight={{base: 700, md: 800}}
      >
        {t(`play.game.${game.name}.title`)}
      </Text>

      <Text
        color={descriptionColors[game.name] ?? 'white'}
        fontSize={{base: 'sm', md: 'md', lg: 'xl'}}
        fontWeight="600"
        w="xl"
        maxW={['full', '75%']}
      >
        {t(`play.game.${game.name}.description`)}
      </Text>
    </Flex>
  );
}

const brandsGradient: Record<CompetitionConfig['name'], string> = {
  fantasy: 'linear(to bottom, #46995F 50%, #46995F80 90%)',
  tactic: 'linear(to bottom, #46995F 50%, #46995F80 90%)',
  'fantasy-sabaho-kora': 'linear(to bottom, #ffc107, #ffc10780)',
  'tactic-erza3': 'linear(to bottom, #368eac, #e7916d)',
  'tactic-spl': 'linear(to bottom, #f29829, #6e9ccf)',
  'fantasy-spl': 'linear(to bottom, #f29829, #6e9ccf)',
  'fantasy-ad': 'linear(to bottom, #fff, #fff)',
  'tactic-boxing': 'linear-gradient(180deg, #090f41, #7a7a7a)',
  'tactic-mma': 'linear-gradient(180deg, #ea4b5e, #000000)',
};

const bgSizes: Partial<Record<CompetitionConfig['name'], string>> = {
  'fantasy-ad': '50%',
};

const mobileBgSizes: Partial<Record<CompetitionConfig['name'], string>> = {
  'fantasy-ad': '35%',
};

const titleColors: Partial<Record<CompetitionConfig['name'], string>> = {
  'fantasy-ad': '#000',
};

const descriptionColors: Partial<Record<CompetitionConfig['name'], string>> = {
  'fantasy-ad': '#000',
};

const borders: Partial<Record<CompetitionConfig['name'], string>> = {
  'fantasy-ad': '2px solid #000',
};
